import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import { scheduleMessage, getScheduled, subscribe, cancelScheduled, loadScheduledFromBackend } from '../../utils/messageScheduler';
import { useSelector } from 'react-redux';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;

const SentMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [showBatchMessageModal, setShowBatchMessageModal] = useState(false);
  const [batchMessage, setBatchMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);

  // Scheduling state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledItems, setScheduledItems] = useState([]);
  const [scheduledItemsFromBackend, setScheduledItemsFromBackend] = useState([]);
  const [dismissedSentNotifications, setDismissedSentNotifications] = useState(() => {
    // Load dismissed notifications from localStorage on mount
    try {
      const saved = localStorage.getItem('dismissedSentNotifications_sent');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      }
    } catch (err) {
      console.error('Failed to load dismissed notifications:', err);
    }
    return new Set();
  });

  const resolveRecipientName = (id) => {
    const u = uniqueUsers.find(u => u.id === id);
    return u?.name || '不明';
  };

  useEffect(() => {
    // Prefill from Redux cache for faster initial paint
    const cachedPages = (state => state.messages?.pagesByKey || {})((_)=>_);
    // Workaround: access via useSelector to avoid breaking hooks rules; we precompute below
    fetchSentMessages();
  }, []);

  // Use Redux cache via selector (must be inside component body)
  const cachedAdminPages = useSelector(state => state.messages?.pagesByKey || {});
  const cachedAdminMessages = useMemo(() => {
    try {
      const all = Object.values(cachedAdminPages)
        .map(p => p?.messages || [])
        .flat();
      // Admin sent: messages sent FROM admin
      return all.filter(m => m?.from_userID?.role === 'admin');
    } catch (_) { return []; }
  }, [cachedAdminPages]);

  useEffect(() => {
    if (cachedAdminMessages.length > 0 && loading) {
      setMessages(cachedAdminMessages);
      setLoading(false);
    }
  }, [cachedAdminMessages]);

  useEffect(() => {
    // Load scheduled messages from backend on mount
    const loadScheduled = async () => {
      try {
        await loadScheduledFromBackend('admin:sent');
        
        // Process any due scheduled messages first
        const token = localStorage.getItem('token');
        if (token) {
          try {
            // Trigger processing of due messages for admin:sent source
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due?source=admin:sent`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Fetch updated scheduled messages
            const response = await axios.get(
              `${API_URL}/api/admin/scheduled-messages?source=admin:sent`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduledItemsFromBackend(response.data.data || []);
            
            // Load dismissed notifications from localStorage
            try {
              const saved = localStorage.getItem('dismissedSentNotifications_sent');
              if (saved) {
                const parsed = JSON.parse(saved);
                setDismissedSentNotifications(new Set(parsed));
              }
            } catch (err) {
              console.error('Failed to load dismissed notifications:', err);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
        }
      } catch (err) {
        console.error('Failed to load scheduled messages:', err);
      }
    };
    loadScheduled();

    // subscribe to scheduler updates for this context
    const unsubscribe = subscribe((items) => {
      setScheduledItems(items.filter((it) => it.source === 'admin:sent'));
    });
    setScheduledItems(getScheduled().filter((it) => it.source === 'admin:sent'));
    
    // Refresh backend data periodically and process due messages
    const refreshInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Process due messages for admin:sent source
          try {
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due?source=admin:sent`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Show notification if messages were sent (only if user is on the page)
            if (processResponse.data.recentlySent > 0) {
              console.log(`${processResponse.data.recentlySent}件の予約メッセージを送信しました`);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
          
          // Fetch updated scheduled messages from backend
          try {
            const response = await axios.get(
              `${API_URL}/api/admin/scheduled-messages?source=admin:sent`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduledItemsFromBackend(response.data.data || []);
          } catch (err) {
            console.error('Failed to fetch scheduled messages:', err);
          }
          
          // Sync with messageScheduler
          await loadScheduledFromBackend('admin:sent');
        }
      } catch (err) {
        console.error('Failed to refresh scheduled messages:', err);
      }
    }, 30000); // Refresh every 30 seconds
    
    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    // Debug: Log the first message to understand structure
    if (messages.length > 0) {
      console.log('First message structure:', messages[0]);
      console.log('to_userID structure:', messages[0].to_userID);
    }
    
    // Extract unique users from messages - fix the user extraction logic
    const users = [...new Set(messages.map(msg => msg.to_userID?._id))].filter(Boolean);
    console.log('Extracted user IDs:', users);
    
    const userDetails = users.map(userId => {
      const message = messages.find(msg => msg.to_userID?._id === userId);
      return {
        id: userId,
        name: message?.to_userID?.name || message?.to_userID?.userID || '不明',
        email: message?.to_userID?.email || '',
        userID: message?.to_userID?.userID || '',
        photoLink: message?.to_userID?.photoLink
      };
    });
    console.log('User details:', userDetails);
    setUniqueUsers(userDetails);
    
    // Filter messages based on selected user - ensure proper filtering
    if (selectedUser === '') {
      setFilteredMessages(messages);
    } else {
      const filtered = messages.filter(msg => msg.to_userID?._id === selectedUser);
      console.log('Filtered messages for user', selectedUser, ':', filtered.length);
      setFilteredMessages(filtered);
    }
  }, [messages, selectedUser]);

  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '☀️', '🌙', '⭐', '🌟', '✨', '💫', '🌈', '☁️', '🌤️', '🌅'
  ];

  const handleBatchMessage = () => {
    setShowBatchMessageModal(true);
    setBatchMessage('');
    setSelectedUsers([]);
  };

  const addEmoji = (emoji) => {
    console.log("sentmessage", emoji);
    setBatchMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    setSelectedUsers(uniqueUsers.map(user => user.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const sendBatchMessage = async () => {
    if (!batchMessage.trim() && selectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    if (selectedUsers.length === 0) {
      alert('送信先のユーザーを選択してください');
      return;
    }

    try {
      const isFuture = enableSchedule && scheduledAt && new Date(scheduledAt) > new Date();
      if (isFuture) {
        await scheduleMessage({
          recipients: selectedUsers,
          content: batchMessage,
          images: selectedImages,
          scheduledAt,
          source: 'admin:sent',
          type: 'batch',
          needsPointDeduction: true, // Enable point deduction for scheduled bulk messages
        });
        alert(`予約しました (${selectedUsers.length}人)`);
        setShowBatchMessageModal(false);
        setBatchMessage('');
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setSelectedUsers([]);
        setEnableSchedule(false);
        setScheduledAt('');
        return;
      }

      setSendingBatch(true);
      const token = localStorage.getItem('token');
      
      // Deduct points for each recipient before sending
      try {
        const pointCheckResponse = await axios.get(`${API_URL}/api/point-costs`);
        const sendMessageCost = pointCheckResponse.data.find(cost => cost.actionName === 'send_message');
        const costPerMessage = sendMessageCost?.pointCost ?? 0;
        const totalCost = costPerMessage * selectedUsers.length;

        if (costPerMessage > 0) {
          // Check if user has enough points for all messages
          const deductResponse = await axios.post(`${API_URL}/api/users/deduct-points`, {
            actionName: 'send_message',
            quantity: selectedUsers.length // Send quantity for bulk deduction
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!deductResponse.data?.success) {
            alert(`ポイントが不足しています。\n必要ポイント: ${totalCost}pt\n所持ポイント: ${deductResponse.data?.available ?? ''}pt`);
            setSendingBatch(false);
            return;
          }

          // Notify global listener to update points in sidebar
          try {
            window.dispatchEvent(new CustomEvent('points:deducted', { detail: { remainingPoints: deductResponse.data.remainingPoints } }));
          } catch (_) {}
        }
      } catch (err) {
        console.error('Error during point deduction:', err);
        alert(err?.response?.data?.message || 'ポイントの確認/減算に失敗しました');
        setSendingBatch(false);
        return;
      }

      // Send messages to all selected users
      const promises = selectedUsers.map(userId =>
        axios.post(`${API_URL}/api/messages/recipientId`, {
          userId: userId,
          content: batchMessage,
          images: selectedImages
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(promises);
      alert(`${selectedUsers.length}人のユーザーにメッセージを送信しました`);
      setShowBatchMessageModal(false);
      setBatchMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setSelectedUsers([]);
      fetchSentMessages();
    } catch (error) {
      console.error('Error sending batch message:', error);
      alert('バッチメッセージの送信に失敗しました');
    } finally {
      setSendingBatch(false);
    }
  };

  const fetchSentMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/messages/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (err) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (msg) => {
    setSelectedMessage(msg);
    setShowDetailModal(true);
  };

  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleDismissSentNotification = (itemId) => {
    setDismissedSentNotifications(prev => {
      const newSet = new Set([...prev, itemId]);
      // Save to localStorage for persistence
      try {
        localStorage.setItem('dismissedSentNotifications_sent', JSON.stringify([...newSet]));
      } catch (err) {
        console.error('Failed to save dismissed notifications:', err);
      }
      return newSet;
    });
  };

  // Get sent messages that haven't been dismissed
  const sentNotifications = scheduledItemsFromBackend
    .filter(item => {
      // Include sent messages that haven't been dismissed
      return item.status === 'sent' && !dismissedSentNotifications.has(item._id);
    })
    .sort((a, b) => {
      // Sort by scheduledAt (most recent first)
      return new Date(b.scheduledAt) - new Date(a.scheduledAt);
    });

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>送信済みメッセージ</h2>

      {/* User Search Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '14px',
              minWidth: '300px',
              backgroundColor: 'white'
            }}
          >
            <option value="">すべてのユーザー</option>
            {uniqueUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {/* {user.name} {user.email ? `(${user.email})` : ''} */}
                {user.name}
              </option>
            ))}
          </select>
          <div style={{ color: '#666', fontSize: '14px' }}>
            {selectedUser === '' ?
              `全${messages.length}件` :
              `選択されたユーザー: ${filteredMessages.length}件`
            }
          </div>
          <button
            onClick={handleBatchMessage}
            style={{
              padding: '12px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
          >
            一括メッセージ送信
          </button>
        </div>
      </div>

      {/* Scheduled items panel */}
      <div style={{
        marginBottom: '20px',
        background: '#f8f9fa',
        border: '1px solid #eee',
        borderRadius: '8px',
        padding: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>⏳ 予約中のメッセージ</div>
        
        {/* Sent notifications */}
        {sentNotifications.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
            {sentNotifications.map((item) => (
              <div key={item._id} style={{
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '8px 10px',
                background: '#d4edda'
              }}>
                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#155724', fontWeight: 'bold' }}>✅ メッセージが予約時間に正確に送信されました</span>
                </div>
                <div style={{ fontSize: '12px', color: '#155724' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ marginLeft: '8px' }}>
                    受信者: {Array.isArray(item.recipients) 
                      ? item.recipients.map(r => r.name || r.userID || r).join(', ')
                      : item.recipients}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.content}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => handleDismissSentNotification(item._id)}
                    style={{
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    確認
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pending scheduled messages */}
        {(scheduledItemsFromBackend.filter(item => item.status === 'pending').length === 0 && sentNotifications.length === 0) && scheduledItems.length === 0 ? (
          <div style={{ color: '#7f8c8d', fontSize: '12px' }}>現在、予約はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Show backend pending items first */}
            {scheduledItemsFromBackend
              .filter(item => item.status === 'pending')
              .map((item) => (
              <div key={item._id} style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '6px 8px',
                background: item.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{item.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>
                    受信者: {Array.isArray(item.recipients) 
                      ? item.recipients.map(r => r.name || r.userID || r).join(', ')
                      : item.recipients}
                  </span>
                  {item.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {item.lastError}</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.content}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => { if (window.confirm('この予約を取り消しますか？')) { cancelScheduled(item._id); } }}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
            {/* Show local scheduled items (for backward compatibility) */}
            {scheduledItems
              .filter(it => !scheduledItemsFromBackend.some(backendItem => backendItem._id === it.id || backendItem._id === it._backendId))
              .map((it) => (
              <div key={it.id} style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '6px 8px',
                background: it.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(it.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{it.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>宛先: {resolveRecipientName(Array.isArray(it.recipients) ? it.recipients[0] : it.recipients)}</span>
                  {it.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {it.lastError}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => { if (window.confirm('この予約を取り消しますか？')) { cancelScheduled(it.id); } }}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>読み込み中...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left', width: '25%' }}>送信先</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '20%' }}>送信日時</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '55%' }}>内容</th>
              </tr>
            </thead>
            <tbody>
              {filteredMessages.map((msg) => (
                <tr key={msg._id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                  <td style={{
                    padding: '15px',
                    verticalAlign: 'top',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {msg.to_userID?.photoLink ? (
                        <img 
                          src={msg.to_userID.photoLink.photodatas || msg.to_userID.photoLink} 
                          alt="Avatar" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flexShrink: 0
                          }} 
                        />
                      ) : (
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          flexShrink: 0
                        }}>
                          なし
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                          {msg.to_userID?.name || msg.to_userID?.userID || '不明'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          ID: {msg.to_userID?.userID}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{
                    padding: '15px',
                    fontSize: '12px',
                    color: '#7f8c8d',
                    verticalAlign: 'top',
                    whiteSpace: 'nowrap'
                  }}>
                    {new Date(msg.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td 
                    style={{
                      padding: '15px',
                      verticalAlign: 'top',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '0',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleViewDetail(msg)}
                  >
                    <div style={{
                      lineHeight: '1.5',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '50px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {msg.body}
                      {msg.body && msg.body.length > 100 && (
                        <div style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          background: 'linear-gradient(transparent, white)',
                          height: '50px',
                          width: '100%',
                          pointerEvents: 'none'
                        }}></div>
                      )}
                    </div>
                    {msg.images && msg.images.length > 0 && (
                      <div onClick={(e) => e.stopPropagation()} style={{  marginTop: '5px', maxWidth: '100%', overflow: 'hidden' }}>
                        <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                      </div>
                    )}
                    {/* {msg.body && msg.body.length > 100 && (
                      <div style={{
                        fontSize: '12px',
                        color: '#3498db',
                        marginTop: '5px',
                        fontWeight: 'bold'
                      }}>
                        クリックして詳細を表示
                      </div>
                    )} */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Batch Message Modal */}
      {showBatchMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>一括メッセージ送信</h3>

            {/* User Selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#34495e' }}>送信先ユーザー選択</h4>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={selectAllUsers}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    全選択
                  </button>
                  <button
                    onClick={deselectAllUsers}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    全解除
                  </button>
                </div>
              </div>

              <div style={{
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '10px'
              }}>
                {uniqueUsers.map((user) => (
                  <div key={user.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px',
                    borderBottom: '1px solid #eee'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {user.photoLink ? (
                        <img 
                          src={user.photoLink.photodatas || user.photoLink} 
                          alt="Avatar" 
                          style={{ 
                            width: '30px', 
                            height: '30px', 
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }} 
                        />
                      ) : (
                        <div style={{ 
                          width: '30px', 
                          height: '30px', 
                          borderRadius: '50%', 
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px'
                        }}>
                          なし
                        </div>
                      )}
                      <div>
                        <span style={{ fontWeight: 'bold' }}>{user.name}</span>
                        <span style={{ color: '#666', marginLeft: '10px' }}>(ID: {user.userID || user.id})</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#34495e' }}>
                メッセージ内容
              </label>
              <textarea
                value={batchMessage}
                onChange={(e) => setBatchMessage(e.target.value)}
                placeholder="メッセージを入力してください..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />

              {/* Scheduling controls */}
              <div style={{ marginTop: '10px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={enableSchedule}
                    onChange={(e) => setEnableSchedule(e.target.checked)}
                    style={{ marginRight: '6px' }}
                  />
                  後で送信を予約する
                </label>
              </div>
              {enableSchedule && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              )}
            </div>

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '10px',
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 1fr)',
                gap: '5px',
                zIndex: 1001,
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {emojis.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => addEmoji(emoji)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '5px'
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            {/* Cost Information */}
            {/* {selectedUsers.length > 0 && (
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '5px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <strong>送信情報:</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
                  送信先: {selectedUsers.length}人
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>
                  必要ポイント: 各メッセージのコスト × {selectedUsers.length}人
                </div>
              </div>
            )} */}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBatchMessageModal(false);
                  setBatchMessage('');
                  setSelectedImages([]);
                  setImagePreviewUrls([]);
                  setSelectedUsers([]);
                }}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendBatchMessage}
                disabled={sendingBatch || selectedUsers.length === 0 || (!batchMessage.trim() && selectedImages.length === 0)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: sendingBatch ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  cursor: sendingBatch ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {sendingBatch ? '送信中...' : (enableSchedule && scheduledAt && new Date(scheduledAt) > new Date() ? `予約 (${selectedUsers.length}人)` : `送信 (${selectedUsers.length}人)`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed View Modal */}
      {showDetailModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '600px',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>送信メッセージ詳細</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Recipient Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              flexShrink: 0
            }}>
              {selectedMessage.to_userID?.photoLink ? (
                <img
                  src={selectedMessage.to_userID.photoLink.photodatas || selectedMessage.to_userID.photoLink}
                  alt="Profile"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  なし
                </div>
              )}
              <div>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginBottom: '5px'
                }}>
                  送信先: {selectedMessage.to_userID?.name || 'Unknown'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px',
                  marginBottom: '5px'
                }}>
                  ID: {selectedMessage.to_userID?.userID || 'N/A'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px'
                }}>
                  送信日時: {new Date(selectedMessage.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
            </div>
            
            {/* Message Content */}
            <div style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333', flexShrink: 0 }}>メッセージ内容</h4>
              <div style={{
                lineHeight: '1.6',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
                color: '#333',
                overflow: 'auto',
                flex: 1,
                marginBottom: selectedMessage.images && selectedMessage.images.length > 0 ? '15px' : '0'
              }}>
                {selectedMessage.body}
              </div>
              {selectedMessage.images && selectedMessage.images.length > 0 && (
                <div style={{ flexShrink: 0 }}>
                  <MessageImages images={selectedMessage.images} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentMessages; 